import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, CheckCircle, AlertCircle, XCircle, Eye } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const STATUS_MAP = {
  pending: { label: '审核中', color: 'yellow', icon: Clock },
  approved: { label: '已通过', color: 'green', icon: CheckCircle },
  rejected: { label: '已拒绝', color: 'red', icon: XCircle },
  expired: { label: '已过期', color: 'gray', icon: AlertCircle },
};

const MyApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const data = await api.get('/authorizations/my-applications');
      if (data.success) {
        setApplications(data.data);
      }
    } catch (error) {
      toast.error('获取申请失败');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('zh-CN');
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">我的申请</h1>
        <Link to="/courses" className="btn-primary">
          浏览课程
        </Link>
      </div>

      {applications.length === 0 ? (
        <div className="card p-12 text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">您还没有提交任何申请</p>
          <Link to="/courses" className="btn-primary">
            去浏览课程
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => {
            const statusInfo = STATUS_MAP[app.status] || STATUS_MAP.pending;
            const StatusIcon = statusInfo.icon;
            
            return (
              <div key={app._id} className="card p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold">
                        <Link to={`/courses/${app.course?._id}`} className="hover:text-primary-600">
                          {app.course?.title || '课程已删除'}
                        </Link>
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-700 flex items-center gap-1`}>
                        <StatusIcon className="h-3 w-3" />
                        {statusInfo.label}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-500 space-y-1">
                      <p>申请时间：{formatDate(app.createdAt)}</p>
                      {app.applicantName && <p>申请人：{app.applicantName}</p>}
                      {app.applicantPhone && <p>手机号：{app.applicantPhone}</p>}
                      {app.reviewedAt && <p>审核时间：{formatDate(app.reviewedAt)}</p>}
                      {app.accessDays && <p>授权天数：{app.accessDays} 天</p>}
                      {app.reviewNote && (
                        <p className="text-red-600">审核备注：{app.reviewNote}</p>
                      )}
                      {app.applicationNote && (
                        <p className="text-gray-600">申请理由：{app.applicationNote}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {app.status === 'approved' && app.course?._id && (
                      <Link
                        to={`/courses/${app.course._id}`}
                        className="btn-primary flex items-center gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        开始学习
                      </Link>
                    )}
                    {app.status === 'rejected' && app.course?._id && (
                      <Link
                        to={`/courses/${app.course._id}`}
                        className="btn-secondary flex items-center gap-1"
                      >
                        重新申请
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 申请说明 */}
      <div className="card p-6 bg-blue-50 border border-blue-200">
        <h3 className="font-semibold text-blue-800 mb-2">申请说明</h3>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>提交申请后，讲师或管理员会审核您的申请</li>
          <li>审核通过后，您将获得指定时长的课程访问权限</li>
          <li>授权到期后，您可以重新申请</li>
          <li>如有疑问，请联系客服</li>
        </ul>
      </div>
    </div>
  );
};

export default MyApplications;
